/**
 * SIGN IN.
 *
 * The UI implements NO authentication logic. It collects what the user types, posts it, and renders
 * what came back. It does not decide whether the credentials are good, it does not decide what the
 * user may then see, and it stores no token — the session is a first-party cookie the server sets.
 *
 * 09 §5.8: a failure here is a written Hebrew sentence, never a status code and never an exception.
 */

import { useCallback, useRef, useState } from 'react';
import { useLocale } from '../i18n/LocaleProvider';
import { routes } from '../routes';
import type { FormEvent } from 'react';
import { DeniedError } from '../api/client';
import { callOperation } from '../api/operations';
import { useServerData } from '../api/useServerData';
import { PORTAL_PRESENTATION } from '../shell/landing';
import { DenialNotice } from '../components/DenialNotice';
import { PasswordField } from '../components/PasswordField';
import type { DenyBody } from '../api/contract';

/**
 * The server's four-arm answer to a sign-in. It now lives beside the operation that produces it, in
 * `api/operations.ts`, so the request shape and the response shape are declared together and the
 * mirror test can reconcile both against `apps/api/src/routes/public.ts`.
 */
export type { SignInResponse } from '../api/operations';

export interface SignInScreenProps {
  readonly onSignedIn: () => void;
  /** The principal holds a second factor and must present it. No session exists yet. */
  readonly onMfaRequired: (challengeRef: string) => void;
  /** The principal is REQUIRED to hold one and does not. Enrolment is unavoidable, not offered. */
  readonly onMfaEnrolmentRequired: (challengeRef: string) => void;
}

export function SignInScreen({
  onSignedIn,
  onMfaRequired,
  onMfaEnrolmentRequired,
}: SignInScreenProps): JSX.Element {
  const { t } = useLocale();
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [deny, setDeny] = useState<DenyBody | null>(null);
  const [failedHe, setFailedHe] = useState<string | null>(null);
  const [resetSent, setResetSent] = useState(false);
  /* Pressed with an empty field: what is needed, said once, beside the control. */
  const [resetNeedsLogin, setResetNeedsLogin] = useState(false);
  const loginField = useRef<HTMLInputElement | null>(null);

  /**
   * Ask for a reset mail, and say the same thing however it goes.
   *
   * `resetSent` is set on BOTH arms on purpose. A screen that reported a failure here would be
   * distinguishing outcomes the endpoint spent its design refusing to distinguish — and the one
   * thing a person can act on, "check your inbox", is true in every case worth telling them about.
   */
  async function requestReset(): Promise<void> {
    /*
     * ── NOT DISABLED, ANSWERED. **Owner, section 7, 2026-11-01.** ────────────────────────────
     *
     * The control used to carry `disabled={pending || login.trim() === ''}`, so on arrival it was
     * grey underlined text that did nothing — *"לא מצאתי כפתור לאיפוס סיסמה"*. An unexplained
     * disabled control is the shape this product refuses on every other screen, and it refuses it
     * here the same way: the press is accepted, the field is focused, and the screen says what is
     * missing.
     */
    if (login.trim() === '') {
      setResetNeedsLogin(true);
      loginField.current?.focus();
      return;
    }
    setResetNeedsLogin(false);
    setDeny(null);
    setFailedHe(null);
    try {
      await callOperation('auth.password.reset_request', { body: { login } });
    } catch {
      // Swallowed, and this is the rare place that is correct. See above.
    } finally {
      setResetSent(true);
    }
  }

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setPending(true);
    setDeny(null);
    setFailedHe(null);
    try {
      /*
       * THREE DEFECTS ON THE PRODUCT'S FRONT DOOR, all found by following the wire rather than by
       * reading either side alone. Each was invisible to the other end.
       *
       *   1. The path was `/session`. The declared route is `/api/auth/session`. There is no
       *      `/api/session`, so every sign-in 404'd. **Neither defect is writable here any more:**
       *      the address is built by `callOperation` from the approved baseline, and the body shape
       *      is `OperationBodies['auth.session.create']`, reconciled against the handler's own
       *      field names by `reachability.test.ts`.
       *   2. The body was `{ email, password }`. The route reads `{ login, password }`, so even at
       *      the right URL the credential arrived as `undefined` and the answer would have been the
       *      generic 401 the endpoint returns for a wrong password — indistinguishable from a real
       *      failure, which is exactly what makes it survivable for months.
       *   3. `onSignedIn()` was called UNCONDITIONALLY. `signIn` returns a four-arm union, and two
       *      of those arms are MFA challenges that deliberately set NO COOKIE. The screen would have
       *      announced a successful sign-in to a principal holding no session.
       *
       * A write. It is never retried — see the retry policy in api/client.ts.
       */
      const result = await callOperation('auth.session.create', {
        body: { login, password },
      });

      // The union is exhaustive on purpose (see routes/public.ts): "I forgot the MFA branch" is what
      // defect 3 was, and it does not compile here either.
      switch (result.outcome) {
        case 'SESSION':
          onSignedIn();
          return;
        case 'MFA_REQUIRED':
          onMfaRequired(result.challengeRef);
          return;
        case 'MFA_ENROLMENT_REQUIRED':
          onMfaEnrolmentRequired(result.challengeRef);
          return;
      }
    } catch (err: unknown) {
      if (err instanceof DeniedError) {
        setDeny(err.body);
      } else {
        setFailedHe(t('signin.failed'));
      }
    } finally {
      setPending(false);
    }
  }

  /*
   * WHICH DOOR THIS IS, ASKED OF THE SERVER.
   *
   * `useServerData` keeps loading, loaded and failed apart, and this screen uses that: a portal it
   * has not been told yet renders no lead line at all, rather than the tenant one "because that is
   * the common case". A mislabelled console is a smaller defect than a mislabelled console that
   * looks deliberate.
   */
  const readPortal = useCallback(() => callOperation('portal.identity.read', {}), []);
  const portalRead = useServerData(readPortal);
  /*
   * ── AND `?? null`, WHICH IS NOT DEFENSIVE PADDING. Found 2026-11-04. ──────────────────────
   *
   * The comment above promises that a portal this screen has not been told renders no lead line.
   * That held for the LOADING state and not for an UNKNOWN VALUE: `PORTAL_PRESENTATION[x]` for an
   * `x` outside the map is `undefined`, and the next line reads `.leadHe` off it. The result is a
   * thrown render on the ONE screen that exists before any session — a white front door, not a
   * missing subtitle.
   *
   * TypeScript cannot object: the operation's type says the field is `'TENANT' | 'PLATFORM'`, and
   * that is a claim about the SERVER, which this build may be older or newer than. A value that
   * crosses the wire is data.
   *
   * Caught by the unknown-portal case in `signIn.dom.test.tsx`, which was written for the link
   * below and found this instead.
   */
  const presentation =
    portalRead.result.state === 'loaded'
      ? (PORTAL_PRESENTATION[portalRead.result.data.portal] ?? null)
      : null;

  /*
   * ── REGISTERING IS A CUSTOMER'S ACT, SO IT IS OFFERED ON THE CUSTOMER'S DOOR ONLY ──────────
   *
   * **Owner, 2026-11-04:** *"כפתור ההרשמה העצמית יופיע בפורטל לקוחות הקצה בלבד. הסר אותו ממסך
   * הכניסה של מנהל הפלטפורמה. האפשרות שלך כמנהל להקים לקוח מתוך הפלטפורמה היא פעולה נפרדת."*
   *
   * The link was unconditional, so the PLATFORM console's front door offered a stranger a way to
   * open an organisation. Two different acts wore one control: a customer registering themselves,
   * and an operator provisioning a customer from inside the console — which has its own screen,
   * its own capability and its own audit, and is not this.
   *
   * THE PORTAL IS ALREADY KNOWN HERE. This screen asks the server which door it is in order to
   * name itself, so the answer is read rather than a second question asked, and the one read
   * decides both the lead line and this link.
   *
   * `=== 'TENANT'` and never `!== 'PLATFORM'`: a portal this build has not heard of must not
   * inherit the customer's door by being merely "not the console". For the same reason the link
   * stays hidden while the read is in flight — the screen never shows an action it then takes
   * away, which is the rule `WelcomeScreen` already follows for this same button.
   */
  const offersRegistration =
    portalRead.result.state === 'loaded' && portalRead.result.data.portal === 'TENANT';

  return (
    <main className="signin" dir="rtl">
      {/*
        THE FRONT DOOR NAMES ITSELF, FROM THE SERVER'S ANSWER.
        This said "התחברות לסביבת הארגון" on BOTH hosts, so a Platform Owner arriving at the console
        was greeted by the customer workspace. The text now comes from `portal.identity.read`; until
        it answers, the neutral product name stands alone rather than a guess that might be wrong.
      */}
      <p className="signin__welcome">{t('signin.welcome')}</p>
      <h1 className="signin__title">{presentation?.titleHe ?? 'GL-POINT'}</h1>
      {presentation === null ? null : <p className="signin__lead">{presentation.leadHe}</p>}

      <form className="signin__form" onSubmit={(e) => void submit(e)}>
        {/*
          ── A FOURTH DEFECT ON THIS DOOR: THE FIELD COULD NOT EXPRESS THE CREDENTIAL ───────────
          **Found on the live Staging estate, 2026-10-15, release d7301aa6f411.**

          This was `type="email"` under the label "דואר אלקטרוני", and the product does not
          authenticate an e-mail address. `authentication-store.ts:228` matches ONE column --
          `login_normalised`, the persisted `LOWER(login)` the unique index is on -- and it never
          compares `email`, which it merely SELECTS. `ops/apply/first-owner.sh:160` passes `--login`
          and `--email` as separate arguments, so the first platform owner exists as login `rafi`
          with an address stored beside it.

          So the front door asked for the one value that cannot sign in. Typing `rafi` was refused
          by the BROWSER before any request was made -- `type="email"` makes it `typeMismatch`, and
          no amount of correct server code is reachable through a field that will not submit --
          while typing the address reached a lookup that cannot match it and returned the generic
          401 the endpoint returns for a wrong password. Indistinguishable from a bad credential,
          which is what made it survivable, exactly like defect 2 above.

          `type="text"` is the fix and it STRICTLY WIDENS what can be entered: an e-mail is valid
          text, so an account whose login IS its address is unaffected. The label now names what
          the system actually matches. No route, no body field and no lookup changed -- the wire was
          already `login` and the server was already right.

          `autoCapitalize`/`spellCheck` are off because a phone keyboard offering `Rafi` for a
          credential field is a support call. Case itself is already safe: the store lowercases and
          the column is computed, so this is about what the person sees being typed.
        */}
        <label className="field">
          <span className="field__label">{t('signin.identifier')}</span>
          <input
            className="field__input"
            type="text"
            dir="ltr"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            value={login}
            onChange={(e) => setLogin(e.currentTarget.value)}
            ref={loginField}
            required
          />
        </label>

        {/* §4, 2026-10-25: the eye, through the shared component. See `PasswordField`. */}
        <PasswordField
          labelHe={t('field.password')}
          value={password}
          onChange={setPassword}
          autoComplete="current-password"
          required
        />

        <button type="submit" className="btn btn--primary" disabled={pending}>
          {pending ? t('signin.working') : t('signin.action')}
        </button>
      </form>

      {/*
        ── FORGOT MY PASSWORD. **G-10 in the ledger, closed at the wire.** ────────────────────────

        The ledger's words were: *"a lost authenticator with spent recovery codes is a permanent
        lockout"*. The route was declared and public, and nothing in this app addressed it — so an
        office that lost a password had exactly one route back into their own accounting system,
        which was to telephone somebody.

        ── THE ANSWER IS THE SAME WHETHER OR NOT THE ADDRESS EXISTS ───────────────────────────────

        Deliberately, and the handler enforces it: even a malformed body gets the uniform 202.
        Anything else is an account-enumeration oracle — a stranger typing addresses into this form
        would learn which of them belong to a client of this firm, which is a client list.

        So the screen says "if that address belongs to an account, a message has been sent". It is
        careful phrasing rather than vagueness for its own sake, and the sentence is the same one
        whichever answer came back, because there is only one answer.
      */}
      {resetSent ? (
        <p className="signin__failed" role="status">
          {t('signin.resetSent')}
        </p>
      ) : (
        <>
          {/*
            AN OUTLINED BUTTON AND NOT UNDERLINED TEXT. `btn--nav` is the product's own "this is a
            control, and it is not the primary action" treatment -- it carries a border and a
            marker, which is what `actionAffordance.test.ts` requires of anything that is not the
            page's primary action. `btn--quiet` carries neither, and the Owner read it as prose.
          */}
          <button type="button" className="btn btn--nav" onClick={() => void requestReset()}>
            {t('signin.forgot')}
          </button>
          {resetNeedsLogin ? (
            <p className="signin__failed" role="status">
              {t('signin.forgotNeedsLogin')}
            </p>
          ) : null}
        </>
      )}

      {deny === null ? null : <DenialNotice deny={deny} />}
      {failedHe === null ? null : <p className="signin__failed">{failedHe}</p>}

      {/*
        0032 · THE WAY IN FOR SOMEBODY WHO HAS NO ACCOUNT. Without it the public door has no handle
        on the outside: `/signup` exists and nothing in the product leads to it.

        IT IS SHOWN EVEN WHILE REGISTRATION IS CLOSED, deliberately. The alternative is this screen
        reading the policy to decide whether to draw a link, which is a second public read on the
        one screen rendered before any session exists. The signup page answers a closed door with a
        SENTENCE -- *"הרשמה עצמית אינה פתוחה כרגע"* -- so a person who follows it gets an answer
        rather than a dead end.

        `btn--nav` and not `btn--primary`: it is a LINK, and the Owner's finding of 2026-10-20 is
        that navigation must not wear the action treatment.

        AND IT IS THE CUSTOMER'S DOOR ONLY — see `offersRegistration` above. Closed registration
        still shows it, on that door; the PLATFORM console never does.
      */}
      {!offersRegistration ? null : (
        <p className="signin__alt">
          <a className="btn btn--nav" href={routes.publicSignup}>
            {t('signup.title')}
          </a>
        </p>
      )}
    </main>
  );
}
